import React from 'react';
import { FunctionComponent, useRef, useState } from 'react';
import Slider from 'react-slick';
import SmoothImage from '@xFrame4/components/common/SmoothImage';
import CtaArrowIcon from '../common/CtaArrowIcon';
import { t } from '@xFrame4/common/Utils';
import config from '@config';

interface HomeMainSliderProps
{
    mainSliderThumbnails: MainSliderThumbnail[];
}

/**
 * Uses Slick Slider: https://react-slick.neostack.com/docs/get-started
 */
const HomeMainSlider: FunctionComponent<HomeMainSliderProps> = (props) =>
{
    const sliderRef = useRef<Slider>(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    /** After change slide event handler */
    const afterChangeSlide = (currentSlide: number) => 
    {
        setCurrentSlide(currentSlide);
    };
    
    /**
     * Go to a slide
     * 
     * @param slideNumber The slide number
     */
    const goToSlide = (slideNumber: number) => 
    {
        if (sliderRef.current) 
        {
            sliderRef.current.slickGoTo(slideNumber);
            setCurrentSlide(slideNumber);
        }
    };
    
    /** Slick slider settings */
    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        afterChange: afterChangeSlide,
    };
    
    /** The slides */
    let cmpSlides = props.mainSliderThumbnails.map((mainSliderThumbnail, index) => {
        return (
            <div key={index} className="main-slider-slide">
                <SmoothImage 
                    src={mainSliderThumbnail.image.url}
                    width={mainSliderThumbnail.image.width}
                    height={mainSliderThumbnail.image.height}
                    className="main-slider-image base-box w-100 h-100"
                    alt={mainSliderThumbnail.title}
                />

                <div className="main-slider-image-overlay base-box"></div>
                
                <div 
                    className="main-slider-slide-overlay base-box ps-4 pt-r200 pe-4 ps-lg-5 pt-lg-6 text-white"
                    onClick={() => window.location.href = mainSliderThumbnail.url}
                >
                    <div className="main-slider-slide-title fw-bold mb-2 text-uppercase cursor-pointer">
                        {mainSliderThumbnail.title}
                    </div>
                    <div className="main-slider-slide-subtitle mb-5 cursor-pointer">
                        {mainSliderThumbnail.subtitle}
                    </div>

                    <div className="main-slider-slide-cta d-flex align-items-center cursor-pointer">
                        <span className="me-2">{mainSliderThumbnail.ctaText}</span>
                        <CtaArrowIcon 
                            variant="light-filled"
                            size={8}
                        />
                    </div>
                </div>
            </div>
        );
    });

    /** The slider navigation */
    let cmpSliderNavigation = 
        <div className="main-slider-navigation d-flex justify-content-center">
            {
                props.mainSliderThumbnails.map((mainSliderThumbnail, index) => {
                    return (
                        <div 
                            key={index}
                            className={'main-slider-navigation-item cursor-pointer ' + (currentSlide === index ? 'active' : '')}
                            onClick={() => goToSlide(index)}
                        />
                    );
                })
            }
        </div>;
    
    /** Render */
    return (
        <div className="main-slider">
            <Slider ref={sliderRef} {...sliderSettings}>
                {cmpSlides}
            </Slider>
            {cmpSliderNavigation}
        </div>
    );
}

export type MainSliderThumbnail = {
    title: string;
    subtitle: string;
    ctaText: string;
    url: string;
    image: {
        url: string;
        width: number;
        height: number;
    };
    alterImage?: {
        url: string;
        width: number;
        height: number;
    };
};

export default HomeMainSlider;